import React from "react"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/contexts/AuthContext"
import Splash from "@/components/splash"
import { useNavigate, useParams } from 'react-router-dom'
import { debounce } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Forbidden, NotFound } from "@/components/error"
import { useTimer } from "@/hooks/timer"
import { removeFilesByPydioId } from "@/pages/order/PDF"
import ExamTablesDialog from "@/pages/order/ExamTablesDialog"


const OrderContext = React.createContext()

export function OrderProvider({ children }) {
    const { id } = useParams()

    const [searchParams] = React.useState(new URLSearchParams(window.location.search));
    const [compareOrder, setCompareOrder] = React.useState(searchParams.get('compare') !== null);

    const { user } = useAuth()
    const isAdmin = user?.settings?.isAdmin
    const transcriber = user?.transcriber

    const [order, setOrder] = React.useState({})
    const [loading, setLoading] = React.useState(!!id)
    const [forbidden, setForbidden] = React.useState(false)
    const [notfound, setNotfound] = React.useState(false)

    const { activeTime, isActive, startTimer } = useTimer();

    const [examTablesEditor, setExamTablesEditor] = React.useState(null)
    const [examTables, setExamTables] = React.useState(null)

    React.useEffect(() => {
        async function fetchExamTables() {
            const response = await fetch(`${import.meta.env.APP_API_URL}/generation/exam-tables`, {
                method: "POST"
            })
            const data = await response.json()
            setExamTables(data)
        }

        fetchExamTables()
    }, [])
    
    React.useEffect(() => {
        if (!id){
            setOrder({})
            return
        }
        
        
        fetchOrder(id).then((order) => {
            if (!order) {
                setNotfound(true)
                setLoading(false)
            } else if (
                (
                    isAdmin ||
                    [
                        order.work.transcriber_id,
                        order.work.translater_id,
                        order.work.reviewer_id,
                    ].includes(transcriber.id)
                )
            ) {
                const orderStorage = JSON.parse(localStorage.getItem('order') || 'null');
                const order_ = orderStorage?.pydio_id === order.pydio_id ? {
                    ...order,
                    mandate: {
                        ...order.mandate,
                        form: orderStorage.mandate.form,
                    },
                    sections: Object.keys({
                        ...orderStorage.sections,
                        ...order.sections,
                    }).reduce((a, section) => {
                        if (!(section in orderStorage.sections)) {
                            return {
                                ...a,
                                [section]: order.sections[section]
                            }
                        } else {
                            return {
                                ...a,
                                [section]: orderStorage.sections[section] === "LOADING"
                                ? order.sections[section] ?? orderStorage.sections[section]
                                : orderStorage.sections[section]
                            }
                        }
                    }, {}),
                    sections_comment: orderStorage.sections_comment,
                    is_sections_dirty: orderStorage.is_sections_dirty,
                    transcription: orderStorage.transcription,
                    transcription_not: orderStorage.transcription_not,
                } : order

                setOrder(order_)
                setLoading(false)
                
                removeFilesByPydioId(order_.pydio_id)
                
                if (!isAdmin && !order.finished_at) {
                    startTimer(order_.pydio_id)
                }
            } else {
                setForbidden(true)
                setLoading(false)
            }
        })
    }, [id])

    const debouncedSetOrder = React.useCallback(
        debounce((newOrder) => {
            if (newOrder) {
                localStorage.setItem('order', JSON.stringify(newOrder));
            }
        }, 500),
        []
    );

    React.useEffect(() => {
        debouncedSetOrder(order);
    }, [order, debouncedSetOrder]);

    if (forbidden) {
        return <Forbidden />
    }

    if (notfound) {
        return <NotFound />
    }

    return (
        <OrderContext.Provider value={{ compareOrder, order, setOrder, activeTime, examTables, setExamTablesEditor }}>
            <ExamTablesDialog editor={examTablesEditor} setOpen={setExamTablesEditor} />

            {children}
        </OrderContext.Provider>
    )
}

export function useOrder() {
    const context = React.useContext(OrderContext)

    return context
}

async function fetchOrder(id) {
    const { data: folder } = await supabase
        .from("folders")
        .select("*, company:companies(*), work:works(*), doctor:doctors(*, profile:profiles(*))")
        .eq("pydio_id", id)
        .single()

    if (!folder) {
        return false
    }

    const { data: files } = await supabase.storage.from("folders").list(`${folder.company.id}/${folder.folder_id}`)

    const { data: suggestions } = await supabase
        .from("transcription_suggestions")
        .select("*")
        .eq("folder_pydio_id", folder.pydio_id)
        .is('rc_id', null)
        .single()

    const mandateFile = files.find((document) => (document.name.toLowerCase().includes("mandat") && (document.name.toLowerCase().includes("docx") || document.name.toLowerCase().includes("pdf"))) || document.name === folder.mandate_storage_path?.split('/').pop())
    const audioFile = files.find((document) => document.name === folder.dictation_path?.split("/").pop())
    const documentsFiles = files?.filter(document => !!document.id && document.name !== audioFile?.name && !['dss', 'mp3', 'mp4', 'a4c', 'ds2'].includes(document.name.toLowerCase().split(".").pop()))

    const sections = folder.sections_reviewed || folder.sections || {}

    return {
        id: folder.id,
        pydio_id: folder.pydio_id,
        isLayout: folder.isLayout,
        supported: folder.doctor.id === 14,
        folder_id: folder.folder_id,
        insurance_type: folder.insurance_type?.trim(),
        company: folder.company,
        doctor: folder.doctor,
        patient: folder.patient,
        assessment_date: folder.assessment_date,
        documents: documentsFiles,
        mandate: {
            mandate_storage_path: folder.mandate_storage_path,
            path: mandateFile && `${folder.company.id}/${folder.folder_id}/${mandateFile.name}`,
            form_generated: folder.jsonMandate,
            form: folder.jsonMandateReviewed || folder.jsonMandate,
            reviewed: (folder.isLayout && folder.jsonMandateReviewed?.reviewed) || (folder.jsonMandateReviewed?.reviewed && !!sections.mandate_static) || folder.correction?.extracted_sections,
        },
        transcription_generated: folder.correction?.extracted_sections,
        transcription: folder.correction?.extracted_sections_reviewed || folder.correction?.extracted_sections,
        transcription_not: folder.correction?.not_extracted_sections_text?.trim().split("\n\n"),
        is_transcription_dirty: false,
        is_transcription_reviewed: !!folder.correction?.extracted_sections_reviewed,
        audio: {
            segments: folder.transcription,
            file: audioFile,
            duration: folder.duration,
        },
        completed_at: folder.completed_at,
        finished_at: folder.work.finished_at,
        work: folder.work,
        report_pspdfkit: folder.report_pspdfkit || folder.work.report_pspdfkit,
        report_path: folder.report_path || folder.work.report_path,
        work_report_path: folder.work.report_path,
        sections_generated: folder.sections || {},
        sections,
        sections_comment: folder.work.sections_comment || {},
        is_sections_reviewed: !!folder.sections_reviewed,
        is_sections_dirty: false,
        suggestions: suggestions,
        folder: folder,
        isGenerated: folder.doctor.id === 14 || folder.isLayout
    }
}